<div class="entity-detail-page text-fg relative max-w-[900px]">
  @if (loading()) {
    <div class="detail-loading flex items-center justify-center gap-3 px-8 py-16 text-fg-secondary">
      <ui-spinner size="sm" />
      <span>Loading...</span>
    </div>
  } @else if (error()) {
    <div class="detail-error p-8 text-center">
      <p class="m-0 mb-4 text-[0.9375rem] text-danger-fg">{{ error() }}</p>
      <button type="button" uiButton variant="outline" size="sm" (click)="load()">Retry</button>
    </div>
  } @else if (data(); as obj) {
    <header class="detail-header mb-6 flex items-center justify-between gap-4">
      <h1 class="m-0 font-heading text-3xl leading-tight font-extrabold tracking-tight text-fg">{{ entityConfig.label }} Detail</h1>
      <div class="detail-header-actions flex items-center gap-2">
        <button type="button" uiButton variant="outline" class="size-10 !p-0" (click)="load()" title="Refresh">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
        </button>
        @if (entityConfig.showEditButton !== false) {
          <a uiButton variant="primary" [routerLink]="editLink">Edit</a>
        }
      </div>
    </header>

    <div class="detail-content mb-6 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      @for (key of objectKeys(obj); track key) {
        <div class="detail-row grid grid-cols-[minmax(160px,220px)_1fr] border-b border-surface-container last:border-b-0">
          <div class="detail-key border-r border-surface-container bg-surface-muted px-5 py-4 text-[0.6875rem] font-bold tracking-widest text-muted uppercase">{{ formatKey(key) }}</div>
          <div class="detail-value flex min-h-[52px] flex-col items-start justify-center px-5 py-4 text-[0.9375rem] break-words text-fg-secondary">

            <!-- Boolean -->
            @if (isBoolean(obj[key])) {
              @if (obj[key]) {
                <span class="val-bool inline-flex items-center gap-1 text-sm font-semibold text-success-fg">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Yes
                </span>
              } @else {
                <span class="val-bool inline-flex items-center gap-1 text-sm font-semibold text-danger-fg">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  No
                </span>
              }

            <!-- ID (copiable) -->
            } @else if (isWalletStatementDepositLink(key, obj)) {
              <a class="val-id inline-flex cursor-pointer items-center gap-1.5 font-mono text-sm text-primary transition-colors hover:text-brand hover:underline" [routerLink]="walletStatementDepositLink(obj)">
                {{ obj[key] }}
              </a>
            } @else if (isId(obj[key], key)) {
              <span class="val-id group inline-flex cursor-pointer items-center gap-1.5 font-mono text-sm text-primary transition-colors hover:text-brand hover:underline" (click)="copyToClipboard($any(obj[key]), $event)" [title]="'Click to copy: ' + obj[key]">
                {{ obj[key] }}
                <svg class="copy-icon opacity-40 transition-opacity group-hover:opacity-100" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              </span>

            <!-- Date -->
            } @else if (isDate(obj[key], key)) {
              <span class="val-date text-muted">{{ formatDate($any(obj[key])) }}</span>

            <!-- Number -->
            } @else if (isNumber(obj[key], key)) {
              <span class="val-number font-semibold tabular-nums">{{ formatNumber(obj[key]) }}</span>

            <!-- Choice enum -->
            } @else if (isChoiceEnum(obj[key])) {
              <ui-badge [variant]="choiceBadgeVariant(obj[key])">{{ choiceTitle(obj[key]) }}</ui-badge>

            <!-- Composite cell -->
            } @else if (isCompositeCell(obj[key])) {
              <span class="cell-composite inline-flex max-w-full min-w-0 items-center gap-2.5">
                <span class="cell-composite-text flex min-w-0 flex-col gap-0.5 leading-tight">
                  <span class="cell-composite-primary text-[0.8125rem] font-semibold text-fg">{{ $any(obj[key]).primary || '—' }}</span>
                  @if ($any(obj[key]).secondary) {
                    <span class="cell-composite-sub font-mono text-[0.6875rem] tracking-wide text-muted">{{ $any(obj[key]).secondary }}</span>
                  }
                </span>
              </span>

            <!-- Stacked cell -->
            } @else if (isStackedCell(obj[key])) {
              <span class="cell-stacked inline-flex flex-col gap-0.5 leading-tight">
                <span class="cell-stacked-top text-[0.8125rem] font-semibold text-fg">{{ $any(obj[key]).top || '—' }}</span>
                @if ($any(obj[key]).bottom) {
                  <span class="cell-stacked-bottom font-mono text-xs text-muted">{{ $any(obj[key]).bottom }}</span>
                }
              </span>

            <!-- Amount composite -->
            } @else if (isAmountCurrencyCell(obj[key])) {
              <span class="cell-amount-composite inline-flex items-baseline gap-1.5">
                <span class="cell-amount-composite-value font-semibold text-fg tabular-nums">{{ formatNumber($any(obj[key]).amount) }}</span>
                <span class="cell-amount-composite-currency font-mono text-xs text-muted">{{ $any(obj[key]).currency }}</span>
              </span>

            <!-- Sub-object (key-value table) -->
            } @else if (isSubObject(obj[key])) {
              @if (subObjectKeys(obj[key]).length === 0) {
                <span class="val-empty text-muted italic">Empty object</span>
              } @else {
                <table class="val-kv-table w-full border-collapse overflow-hidden rounded-lg border border-border text-[0.8125rem] [&_tr:not(:last-child)_td]:border-b [&_tr:not(:last-child)_td]:border-surface-container">
                  <tbody>
                    @for (subKey of subObjectKeys(obj[key]); track subKey) {
                      <tr>
                        <td class="kv-key w-[1%] border-r border-surface-container bg-surface-muted px-3 py-2 text-[0.6875rem] font-semibold tracking-widest whitespace-nowrap text-muted uppercase">{{ formatKey(subKey) }}</td>
                        <td class="kv-value px-3 py-2 break-words text-fg">
                          @if (isSubObject(subObjectValue(obj[key], subKey))) {
                            <code class="kv-json rounded bg-surface-muted px-1.5 py-0.5 font-mono text-xs break-all text-fg-secondary">{{ formatSubValue(subObjectValue(obj[key], subKey)) }}</code>
                          } @else if (isBoolean(subObjectValue(obj[key], subKey))) {
                            @if (subObjectValue(obj[key], subKey)) {
                              <span class="val-bool inline-flex items-center gap-1 text-sm font-semibold text-success-fg"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Yes</span>
                            } @else {
                              <span class="val-bool inline-flex items-center gap-1 text-sm font-semibold text-danger-fg"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> No</span>
                            }
                          } @else {
                            {{ formatSubValue(subObjectValue(obj[key], subKey)) }}
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              }

            <!-- Array -->
            } @else if (isArray(obj[key])) {
              @if ($any(obj[key]).length === 0) {
                <span class="val-empty text-muted italic">Empty list</span>
              } @else {
                <div class="val-array flex w-full flex-col gap-1.5">
                  @for (item of $any(obj[key]); track $index) {
                    @if (isSubObject(item)) {
                      <table class="val-kv-table val-kv-table-sm mb-2 w-full border-collapse overflow-hidden rounded-lg border border-border text-[0.8125rem] last:mb-0 [&_tr:not(:last-child)_td]:border-b [&_tr:not(:last-child)_td]:border-surface-container">
                        <tbody>
                          @for (itemKey of subObjectKeys(item); track itemKey) {
                            <tr>
                              <td class="kv-key w-[1%] border-r border-surface-container bg-surface-muted px-3 py-2 text-[0.6875rem] font-semibold tracking-widest whitespace-nowrap text-muted uppercase">{{ formatKey(itemKey) }}</td>
                              <td class="kv-value px-3 py-2 break-words text-fg">{{ formatSubValue(subObjectValue(item, itemKey)) }}</td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    } @else {
                      <span class="val-array-item inline-block rounded border border-border bg-surface-muted px-2 py-1 text-[0.8125rem] text-fg">{{ formatSubValue(item) }}</span>
                    }
                  }
                </div>
              }

            <!-- Null -->
            } @else if (obj[key] === null || obj[key] === undefined) {
              <span class="val-empty text-muted italic">—</span>

            <!-- Default text -->
            } @else {
              {{ obj[key] }}
            }
          </div>
        </div>
      }
    </div>
  }

  <button type="button" class="back inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[0.9375rem] font-semibold text-fg-secondary transition-colors hover:text-brand" (click)="goBack()">← Back</button>

  <!-- Copy toast -->
  @if (copyToast()) {
    <div class="copy-toast fixed bottom-8 left-1/2 z-[10000] -translate-x-1/2 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white shadow-lg">Copied!</div>
  }
</div>
